/**
 *  @namespace account/session
 */
import storage from '~/utils/lib/core/storage'
import { any, isEmpty, noop, notEmpty, pipe_, prop, tapThen, values } from '~/utils/lib/core/prelude'
import { CACHE_MAX_PERIOD, CLIENT_VERSION, ERRORS, EVENTS, observer, STORE_PREFIX } from '~/utils/infra/account/types'
import { now } from '~/utils/lib/core/datetime'

export const useStore_account_session = defineStore('account/session', {
  state: () => ({
    accessToken: '',
    refreshToken: '',
    mfaToken: '',
  }),
  getters: {
    has: state => pipe_(
      state,
      prop('accessToken'),
      notEmpty
    ),
  },
  actions: {
    /// Actions
    ///
    /**
     * @function create
     * @param {object} options
     * @param {string} options.accessToken
     * @param {string} options.refreshToken
     * @param {boolean} options.isPersistent
     * @return {Promise<void>}
     */
    create(
      { accessToken, refreshToken, isPersistent }
    ) {
      return this.destroy()
        .then(() => {
          this.$patch({ accessToken, refreshToken })

          return Promise.all([
              storage.set(STORE_PREFIX, 'accessToken', accessToken),
              storage.set(STORE_PREFIX, 'refreshToken', refreshToken),
              storage.set(STORE_PREFIX, 'clientVersion', CLIENT_VERSION),
            ])
        })
        .then(() => this.updateExpiration(isPersistent))
    },
    /**
     * @function updateExpiration
     * @param {boolean} isPersistent
     * @return {Promise<void>}
     */
    updateExpiration(isPersistent) {
      const updatedAt = now({ outputFormat: 'x' })

      const expiredAt = now({
        outputFormat: 'x',
        additional: [
          isPersistent ? CACHE_MAX_PERIOD : 0,
          'milliseconds'
        ]
      })

      return Promise.all([
          storage.set(STORE_PREFIX, 'updatedAt', updatedAt),
          storage.set(STORE_PREFIX, 'expiredAt', expiredAt),
        ])
        .then(noop)
    },
    /**
     * @function refresh
     * @param {{refreshToken: string, accessToken: string}} options
     * @return {Promise<void>}
     */
    refresh({ refreshToken, accessToken }) {
      this.$patch({ refreshToken, accessToken })

      return Promise.all([
          storage.set(STORE_PREFIX, 'refreshToken', refreshToken),
          storage.set(STORE_PREFIX, 'accessToken', accessToken),
        ])
        .then(noop)
    },
    /**
     * @function destroy
     * @param {boolean} [hardDestroy=false]
     * @return {Promise<void>}
     */
    destroy(hardDestroy = false) {
      this.$reset()

      return storage.destroyStore(STORE_PREFIX)
        .then(() => {
          if (hardDestroy)
            return observer.emit(EVENTS.ON_STORE_SESSION_DESTROY)
        })
        .then(noop)
    },
    /**
     * @function retrieve
     * @return {Promise<import('~/utils/infra/account/types').AccountSessionData>}
     */
    retrieve() {
      return Promise.all([
          storage.get(STORE_PREFIX, 'accessToken'),
          storage.get(STORE_PREFIX, 'refreshToken'),
          storage.get(STORE_PREFIX, 'clientVersion'),
          storage.get(STORE_PREFIX, 'updatedAt'),
          storage.get(STORE_PREFIX, 'expiredAt'),
        ])
        .then(results => ({
          accessToken   : results[0],
          refreshToken  : results[1],
          clientVersion : results[2],
          updatedAt     : results[3],
          expiredAt     : results[4],
        }))
        .then(tapThen(validateData))
    },
  }
})

/**
 *  @name validateData
 *  @param {import('~/utils/infra/account/types').AccountSessionData} data
 *  @throws {Error}
 *  @return {void}
 */
function validateData(data) {
  const { expiredAt, clientVersion } = data
  const now_ = now({ outputFormat: 'x' })

  if (isEmpty(clientVersion))
    throw new Error(ERRORS.NO_CACHE_RETRIEVED)

  if (clientVersion !== CLIENT_VERSION)
    throw new Error(ERRORS.CACHE_MAJOR_VERSION_MISMATCHED)

  if (any(isEmpty, values(data)))
    throw new Error(ERRORS.CACHE_INTEGRITY_COMPROMISED)

  if (expiredAt < now_)
    throw new Error(ERRORS.CACHE_EXPIRATION_REACHED)
}

